/**
 * Selected-board state — the "which board am I editing?" anchor for the
 * top navigation. localStorage-backed so the user's choice survives
 * refreshes; cleared on sign-out.
 *
 * Tabs in TopNav that are board-scoped (Create a Button, Colors,
 * Modes, Manage, Parental Settings) read this value to know where to
 * deep-link. If it's null, the tab clicks redirect to /pick-board with
 * a banner.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "sonspeak_selected_board_v1";

export function getSelectedBoardId() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function setSelectedBoardId(id) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  // Notify listeners — useSelectedBoard() in other components.
  window.dispatchEvent(new CustomEvent("sonspeak:selected-board", { detail: { id } }));
}

export function clearSelectedBoardId() {
  setSelectedBoardId(null);
}

export function useSelectedBoard() {
  const [id, setId] = useState(() => getSelectedBoardId());
  useEffect(() => {
    const onEvt = (e) => setId(e.detail?.id ?? null);
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setId(e.newValue || null);
    };
    window.addEventListener("sonspeak:selected-board", onEvt);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("sonspeak:selected-board", onEvt);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  const set = useCallback((next) => setSelectedBoardId(next), []);
  return [id, set];
}
